import { FormEvent, useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
    Button,
    Container,
    LoadingOverlay,
    Paper,
    PasswordInput,
    Text,
    Title,
} from "@mantine/core";
import { Auth } from "aws-amplify";
import type { TFunction } from "i18next";
import { TranslationKeys } from "src/types/i18next";
import { handleError } from "src/utils/error";

import { FormItem } from "../components/FormItem";

const translateAuthErrorMessage = (t: TFunction, message: string): TranslationKeys | undefined => {
    if (message.indexOf("empty") >= 0) {
        return "password_error_empty";
    }

    if (message.indexOf("long enough") >= 0) {
        return "password_error_length";
    }

    if (message.indexOf("uppercase character") >= 0) {
        return "password_error_uppercase";
    }

    if (message.indexOf("numeric character") >= 0) {
        return "password_error_lowercase";
    }

    if (message.indexOf("symbol character") >= 0) {
        return "password_error_symbol";
    }

    return undefined;
};

export interface PasswordResetPageProps { }
export const PasswordResetPage = ({ }: PasswordResetPageProps) => {
    const { t } = useTranslation(["common"]);
    const navigate = useNavigate();

    const [searchParams] = useSearchParams();

    const [email, setEmail] = useState("");
    const [code, setCode] = useState("");
    const [newPassword, setNewPassword] = useState("");

    const [isReseting, setIsReseting] = useState(false);
    const [isDone, setIsDone] = useState(false);

    const token = searchParams.get("token");

    useEffect(() => {
        try {
            const { email, code } = JSON.parse(atob(token));

            setEmail(email);
            setCode(code);
        } catch (err) {
            handleError(err, { context: { token }, silent: true });
        }
    }, [token]);

    const handleSubmit = useCallback(async (event: FormEvent<HTMLFormElement>) => {
        event?.stopPropagation?.();
        event?.preventDefault?.();

        setIsReseting(true);

        try {
            await Auth.forgotPasswordSubmit(email, code, newPassword);
            setIsDone(true);
        } catch (err) {
            let message: TranslationKeys | undefined;
            if (err?.name === "AuthError") {
                message = translateAuthErrorMessage(t, String(err?.log));
            } else if (err?.name === "InvalidPasswordException" || err?.name === "LimitExceededException") {
                message = translateAuthErrorMessage(t, JSON.stringify(err));
            } else if (err?.name === "ExpiredCodeException") {
                message = "resetpassword_error_expired";
            }

            handleError(err, {
                context: { token },
                title: { key: "resetpassword_failed", namespace: "common" },
                message: { key: message, namespace: "common" },
            });
        } finally {
            setIsReseting(false);
        }
    }, [email, code, newPassword, token, t]);

    return (
        <Container size={420} my={40}>
            <Title
                align="center"
                sx={(theme) => ({
                    fontFamily: `Greycliff CF, ${theme.fontFamily}`,
                    fontWeight: 900,
                })}
            >
                {t("signin_title")}
            </Title>
            <Text
                color="dimmed"
                size="sm"
                align="center"
                mt={5}
            >
                {t("signin_subtitle")}
            </Text>
            <Paper
                mt={30}
                p={30}
                pos="relative"
                shadow="md"
                radius="md"
                component="form"
                onSubmit={handleSubmit}
                withBorder
            >
                <LoadingOverlay
                    overlayBlur={2}
                    radius="md"
                    visible={isReseting}
                />
                <Title mb="sm" order={3} align="center">
                    {t("resetpassword")}
                </Title>
                {isDone ? (
                    <>
                        <Text mb="md" ta="center">
                            {t("resetpassword_done")}
                        </Text>
                        <Button
                            onClick={() =>
                                navigate("/")}
                            fullWidth
                        >
                            {t("signin")}
                        </Button>
                    </>
                ) : (
                    <>
                        <FormItem mb="xs" label={t("email")}>
                            {email}
                        </FormItem>
                        <PasswordInput
                            label={t("password_new")}
                            placeholder={t("password_new")}
                            value={newPassword}
                            onChange={event =>
                                setNewPassword(event?.target?.value)}
                        />
                        <Button
                            mt="md"
                            type="submit"
                            fullWidth
                        >
                            {t("resetpassword_setnew")}
                        </Button>
                    </>
                )}
            </Paper>
        </Container>
    );
};
